/**
 * Created by Jerker Nord on 2021-04-30.
 */

import {ISymbol, ISymbolPosition} from "../../../core/reel/ReelInterfaces";
import {gsap, TimelineLite} from "gsap";
import {TheBorderStatics} from "../../TheBorderStatics";
import {TheBorderSymbol, TheBorderSymbolStates} from "../../reels/TheBorderSymbol";
import {TheBorder} from "../../TheBorder";
import {SymbolUtils} from "../../utils/SymbolUtils";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {TheBorderXBombSymbol} from "../../reels/TheBorderXBombSymbol";
import {TheBorderUtils} from "../../utils/TheBorderUtils";
import {TheBorderXNudgeSymbol} from "../../reels/TheBorderXNudgeSymbol";
import {TheBorderXSplitSymbol} from "../../reels/TheBorderXSplitSymbol";
import {TheBorderXWaysSymbol} from "../../reels/TheBorderXWaysSymbol";
import {TheBorderScatterSymbol} from "../../reels/TheBorderScatterSymbol";
import {TheBorderSound} from "../../sound/TheBorderSound";
import {TheBorderGameMode} from "../../server/TheBorderGameMode";

export class TheBorderReelView extends PIXI.Container {
    private static SYMBOLS_FILL_UP_DURATION: number = 0.2;
    private static SYMBOLS_FILL_UP_UNBLUR_DURATION: number = 0.1;
    private readonly _reelId: number;
    private _isAvalancheRestore: boolean;
    private _allInSymbols: ISymbol[] = [];
    private _allOutSymbols: ISymbol[] = [];
    private _isBombLandSoundPlayed: boolean = false;
    private _isSplitLandSoundPlayed: boolean = false;
    private _isWaysLandSoundPlayed: boolean = false;
    private _isNudgeLandSoundPlayed: boolean = false;

    constructor(id: number) {
        super();

        this.name = "Reel " + id;
        this._reelId = id;
        this.position = TheBorderStatics.reelPositions[id];
        this._isAvalancheRestore = false;
    }

    private _reelHidden: boolean = false;

    get reelHidden(): boolean {
        return this._reelHidden;
    }

    private _collapsedReel: ISymbol[] = [];

    get collapsedReel(): ISymbol[] {
        return this._collapsedReel;
    }

    private static resetSymbol(symbol: ISymbol): void {
        if (symbol.symbolName == "X_BOMB" || symbol.symbolName == "X_BOMB_H") {
            (<TheBorderXBombSymbol>symbol).reset();
        } else if (symbol.symbolName == "X_SPLIT_SPENT" || symbol.symbolName == "X_SPLIT_SPENT_H") {
            (<TheBorderXSplitSymbol>symbol).reset();
        } else if (symbol.symbolName == "WR" || symbol.symbolName == "WR_H") {
            (<TheBorderXNudgeSymbol>symbol).reset();
        } else if (symbol.symbolName == "BN_H") {
            (<TheBorderScatterSymbol>symbol).reset();
        } else if (symbol.transformed) {
            (<TheBorderXWaysSymbol>symbol).reset();
        } else {
            (<TheBorderSymbol>symbol).reset();
        }
    }

    public onStartSpin(): void {
        this._isBombLandSoundPlayed = false;
        this._isSplitLandSoundPlayed = false;
        this._isWaysLandSoundPlayed = false;
        this._isNudgeLandSoundPlayed = false;
    }

    public clear(){
        while(this._allInSymbols.length > 0){
            const sym = this._allInSymbols.pop();
            if (sym){
                this.removeAvalancheSymbol(sym);
            }
        }
        while(this._allOutSymbols.length > 0){
            const sym = this._allOutSymbols.pop();
            if (sym){
                this.removeAvalancheSymbol(sym);
            }
        }
    }

    public initReel(symbols: string[], isAvalancheRestore: boolean, isBonusIntroRestore: boolean): void {
        this._isAvalancheRestore = isAvalancheRestore;
        for (let i: number = 0; i < symbols.length; i++) {
            let sizeMultiplier: number = 1;
            if (symbols[i].indexOf("_H") > -1) {
                sizeMultiplier = 0.5;
            }
            if (!isAvalancheRestore || isBonusIntroRestore) {
                const symbol: ISymbol = this.createSymbol(symbols[i], TheBorderSymbolStates.COLLAPSE_LANDING, {
                    reelId: this._reelId,
                    startPosition: (i + 1),
                    targetPosition: (i + 1),
                    sizeMultiplier: sizeMultiplier
                });
                this._allOutSymbols.push(symbol);
            } else if (isAvalancheRestore) {
                if (symbols[i] !== "X") {
                    const symbol: ISymbol = this.createSymbol(symbols[i], TheBorderSymbolStates.COLLAPSE_LANDING, {
                        reelId: this._reelId,
                        startPosition: (i + 1),
                        targetPosition: (i + 1),
                        sizeMultiplier: sizeMultiplier
                    });
                    if (TheBorder.model.allSize.length > 0) {
                        symbol.splitLevel = TheBorder.model.allSize[this._reelId][i];
                    }
                    this._collapsedReel.push(symbol);
                }
            }
        }

        //Adjusting symbol yPos of restored reel
        if ((isAvalancheRestore && this._collapsedReel.length > 0)) {
            this._collapsedReel[this._collapsedReel.length - 1].position.y = 600 - (TheBorderStatics.symbolHeight * this._collapsedReel[this._collapsedReel.length - 1].sizeMultiplier);
            if (this._collapsedReel.length > 1) {
                for (let i: number = this._collapsedReel.length - 2; i > -1; i--) {
                    this._collapsedReel[i].position.y = this._collapsedReel[i + 1].position.y - (TheBorderStatics.symbolHeight * this._collapsedReel[i].sizeMultiplier);
                }
            }
        } else if (!isAvalancheRestore){
            this._allOutSymbols[this._allOutSymbols.length - 1].position.y = 600 - (TheBorderStatics.symbolHeight * this._allOutSymbols[this._allOutSymbols.length - 1].sizeMultiplier);
            if (this._allOutSymbols.length > 1) {
                for (let i: number = this._allOutSymbols.length - 2; i > -1; i--) {
                    this._allOutSymbols[i].position.y = this._allOutSymbols[i + 1].position.y - (TheBorderStatics.symbolHeight * this._allOutSymbols[i].sizeMultiplier);
                }
            }
        }

        this.moveScatterToTop(this._allOutSymbols);
        this.moveScatterToTop(this._collapsedReel);
    }

    public hideReelUntilAllIn(): void {
        this.visible = false;
        this._reelHidden = true;
    }

    //Range = 50 for half symbol, 100 for full symbol
    public getSymbolsClosestToYPos(yPos: number, range: number): ISymbol[] {
        const reelPosition: ISymbol[] = [];
        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            //Symbol on same YPos found (reel is not offset)
            if (this._allOutSymbols[i].position.y == yPos) {
                reelPosition.push(this._allOutSymbols[i]);
                //Symbol found is half but range is full symbol
                if (this._allOutSymbols[i + 1] && this._allOutSymbols[i].sizeMultiplier === 0.5 && range === 100) {
                    reelPosition.push(this._allOutSymbols[i + 1]);
                }
            }
        }
        //No symbols found on same yPos. Reel is offset.
        if (reelPosition.length == 0 && range == 100) {
            for (let i: number = 0; i < this._allOutSymbols.length; i++) {
                //Looking for symbol, half symbol offset up
                if (this._allOutSymbols[i].position.y == yPos - 50) {
                    reelPosition.push(this._allOutSymbols[i]);
                    if (this._allOutSymbols[i + 1]) {
                        reelPosition.push(this._allOutSymbols[i + 1]);
                    }
                }
            }
        }

        //No symbols found on same yPos. Reel is offset. for half symbol
        if (reelPosition.length == 0 && range == 50) {
            for (let i: number = 0; i < this._allOutSymbols.length; i++) {
                //Looking for symbol, half symbol offset up
                if (this._allOutSymbols[i].position.y == yPos - 50) {
                    reelPosition.push(this._allOutSymbols[i]);
                }
            }
        }
        return reelPosition;
    }

    public getSymbol(position: number): ISymbol {
        if (position > (this._allOutSymbols.length - 1)) {
            //console.log("Illegal symbol position. Position " + position + " symbols on reel " + this._reelId + " is " + this._allOutSymbols.length);

            // ToDO remove this later
            position = (this._allOutSymbols.length - 1);
        }
        return this._allOutSymbols[position];
    }

    public getAllSymbols(): ISymbol[] {
        return this._allOutSymbols;
    }

    public createSymbol(symbolName: string, state: TheBorderSymbolStates, symbolPositionData: ISymbolPosition): ISymbol {
        const symbol: TheBorderSymbol = SymbolUtils.createSymbol(symbolName, state, symbolPositionData);
        symbol.position.set(0, (symbolPositionData.startPosition - 1) * (TheBorderStatics.symbolHeight * symbolPositionData.sizeMultiplier));
        symbol.reelTargetPosition = symbolPositionData.targetPosition;
        symbol.reelPosition = symbolPositionData.startPosition;
        symbol.reelId = symbolPositionData.reelId;
        this.addChild(symbol);
        symbol.sizeMultiplier = symbolPositionData.sizeMultiplier;
        symbol.setCurrentStateSpriteTexture();
        symbol.setNewStateSpriteTexture();
        return symbol;
    }

    public addNewSymbolToReel(position: number, newSymbolName: string, size: number, splitLevel: number = 1, hidden?: boolean): ISymbol {
        let newSymbol: ISymbol = this.createSymbol(newSymbolName, TheBorderSymbolStates.NORMAL, {
            reelId: this._reelId,
            startPosition: (this._allOutSymbols[position].reelPosition + 1),
            targetPosition: (this._allOutSymbols[position].reelPosition + 1),
            sizeMultiplier: size
        });
        if (hidden && hidden == true) {
            (<TheBorderSymbol>newSymbol).hide();
        }
        newSymbol.splitLevel = splitLevel;

        this._allOutSymbols.splice((position + 1), 0, newSymbol);

        //Adjusting reel position of all symbols
        for (let i: number = (position + 2); i < this._allOutSymbols.length; i++) {
            this._allOutSymbols[i].reelPosition++;
            this._allOutSymbols[i].reelTargetPosition++;
        }

        return newSymbol;
    }

    public makeTopSymbolHalf(): void {
        const fullTopSymbol: ISymbol = this.getFirstFullTopSymbol();

        fullTopSymbol.symbolName = fullTopSymbol.symbolName + "_H";
        fullTopSymbol.sizeMultiplier = 0.5;
        if (fullTopSymbol.symbolName === "X_SPLIT_H") {
            (<TheBorderXSplitSymbol>fullTopSymbol).getIdleAnimation();
        }
        else if (fullTopSymbol.symbolName === "X_BOMB_H") {
            (<TheBorderXBombSymbol>fullTopSymbol).getIdleAnimation();
        } else if (fullTopSymbol.symbolName === "BN_H") {
            (<TheBorderScatterSymbol>fullTopSymbol).getIdleAnimation();
        } else if (fullTopSymbol.symbolName === "WR_H") {
            (<TheBorderXNudgeSymbol>fullTopSymbol).getIdleAnimation();
        }

        (<TheBorderSymbol>fullTopSymbol).setNewStateSpriteTexture();
        (<TheBorderSymbol>fullTopSymbol).setCurrentStateSpriteTexture();
    }

    public makeTopAndBottomSymbolHalf(): void {
        const fullTopSymbol: ISymbol = this.getFirstFullTopSymbol(),
            fullBottomSymbol: ISymbol = this.getFirstFullBottomSymbol();

        fullTopSymbol.symbolName = fullTopSymbol.symbolName + "_H";
        fullTopSymbol.sizeMultiplier = 0.5;
        if (fullTopSymbol.symbolName === "X_SPLIT_H") {
            (<TheBorderXSplitSymbol>fullTopSymbol).getIdleAnimation();
        } else if (fullTopSymbol.symbolName === "X_BOMB_H") {
            (<TheBorderXBombSymbol>fullTopSymbol).getIdleAnimation();
        } else if (fullTopSymbol.symbolName === "BN_H") {
            (<TheBorderScatterSymbol>fullTopSymbol).getIdleAnimation();
        } else if (fullTopSymbol.symbolName === "WR_H") {
            (<TheBorderXNudgeSymbol>fullTopSymbol).getIdleAnimation();
        }
        (<TheBorderSymbol>fullTopSymbol).setCurrentStateSpriteTexture();
        (<TheBorderSymbol>fullTopSymbol).setNewStateSpriteTexture();

        fullBottomSymbol.symbolName = fullBottomSymbol.symbolName + "_H";
        fullBottomSymbol.sizeMultiplier = 0.5;
        if (fullBottomSymbol.symbolName === "X_SPLIT_H") {
            (<TheBorderXSplitSymbol>fullBottomSymbol).getIdleAnimation();
        } else if (fullBottomSymbol.symbolName === "X_BOMB_H") {
            (<TheBorderXBombSymbol>fullBottomSymbol).getIdleAnimation();
        } else if (fullBottomSymbol.symbolName === "BN_H") {
            (<TheBorderScatterSymbol>fullBottomSymbol).getIdleAnimation();
        } else if (fullBottomSymbol.symbolName === "WR_H") {
            (<TheBorderXNudgeSymbol>fullBottomSymbol).getIdleAnimation();
        }
        (<TheBorderSymbol>fullBottomSymbol).setCurrentStateSpriteTexture();
        (<TheBorderSymbol>fullBottomSymbol).setNewStateSpriteTexture();
    }

    //This will split one symbol into two
    public splitSymbolIntoTwoHalf(symbol: ISymbol, isMidExpand?: boolean): void {
        if (symbol.symbolName == "X_BOMB") {
            (<TheBorderXBombSymbol>symbol).turnOffSymbolSpine();
            (<TheBorderXBombSymbol>symbol).splitByXSplit = true;
        } else if (symbol.symbolName == "WR") {
            (<TheBorderXNudgeSymbol>symbol).turnOffSymbolSpine();
            (<TheBorderXNudgeSymbol>symbol).splitLevel *= 2;

            return;
        } else if (symbol.symbolName == "BN") {
            (<TheBorderScatterSymbol>symbol).turnOffSymbolSpine();
        }
        if (symbol.symbolName.indexOf("_H") === -1) {
            symbol.symbolName += "_H";
        }
        symbol.sizeMultiplier = 0.5;
        (<TheBorderSymbol>symbol).setCurrentStateSpriteTexture();
        (<TheBorderSymbol>symbol).setNewStateSpriteTexture();
        const newSymbol: ISymbol = this.addNewSymbolToReel(symbol.reelPosition - 1, symbol.symbolName, 0.5, symbol.splitLevel);

        // #385
        if (newSymbol.symbolName === "X_BOMB_H") {
            (newSymbol as TheBorderXBombSymbol).setProps();
        } else if (newSymbol.symbolName === "BN_H") {
            (newSymbol as TheBorderScatterSymbol).setProps();
        }

        this.positionAllSymbolsOnReel(0, this._allOutSymbols, isMidExpand);
    }

    //This will visually position all symbols correct (assuming the correct position and target is set correct)
    public positionAllSymbolsOnReel(duration: number, symbols: ISymbol[] = this._allOutSymbols, isMidExpand?: boolean): void {
        let toY: number,
            targetPos: number[] = [symbols[0].position.y];
        for (let i: number = 1; i < symbols.length; i++) {
            toY = targetPos[i - 1] + (TheBorderStatics.symbolHeight * symbols[i - 1].sizeMultiplier);
            targetPos.push(toY);
            gsap.to(symbols[i].position, {
                duration: duration,
                y: toY
            });
            if (symbols[i].symbolName !== "X_WAYS") {
                // #321, #330
                if (isMidExpand && symbols[i].symbolName.indexOf("BN") === -1 && symbols[i].symbolName.indexOf("X_BOMB") === -1 && symbols[i].symbolName.indexOf("WR") === -1) {
                    if (((<TheBorderSymbol>symbols[i]).topLayerContainer.children[0] as PIXI.Container).children.length) {
                        ((<TheBorderSymbol>symbols[i]).topLayerContainer.children[0] as PIXI.Container).children[0].position.y = 50;
                    }
                }
                gsap.to((<TheBorderSymbol>symbols[i]).topLayerContainer.position, {
                    duration: duration,
                    y: toY
                });
            }

            // #299, #310
            if (symbols[i].symbolName === "X_SPLIT" || symbols[i].symbolName === "X_SPLIT_H") {
                (symbols[i] as TheBorderXSplitSymbol).turnOffSymbolSpine();
            }
        }
    }

    public getAllOutAnimation(): TimelineLite {
        const fastSpin: boolean = (TheBorder.apiPlugin.settings.get(APISetting.FAST_SPIN, false));
        const returnTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        while (this._allOutSymbols.length > 0) {
            const symbol: TheBorderSymbol = <TheBorderSymbol>this._allOutSymbols.pop()!;
            symbol.isSpinOut = true;
            if (symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.animateAllOut, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                timeline.add([
                    gsap.timeline({delay: fastSpin ? TheBorderStatics.mainReelAllOutBlurDelayFastSpin + ((6 - symbol.reelPosition) * 0.01) : (TheBorderStatics.mainReelAllOutBlurDelay + ((6 - symbol.reelPosition) * 0.01))}).add((<TheBorderSymbol>symbol).getStateAnimation(TheBorderSymbolStates.SPIN_OUT, fastSpin ? 0 : 0.08)),
                    gsap.to(symbol.position, {
                        duration: fastSpin ? TheBorderStatics.mainReelAllOutTotalSpinDurationFastSpin : TheBorderStatics.mainReelAllOutTotalSpinDuration,
                        y: symbol.position.y + 600,
                        ease: "sine.in",
                        delay: TheBorderStatics.mainReelSpinStartSymbolDelay * symbol.reelPosition
                    })
                ]);
                timeline.add(() => this.removeAvalancheSymbol(symbol));
                subTimelines.push(timeline);
            }
        }
        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public getFillUpReelAnimation(): gsap.core.Timeline {
        let reelLength: number = TheBorder.model.reels[this._reelId].length;
        const numberOfFillUpSymbols: number = reelLength - this._collapsedReel.length;

        // Don't do if this is an avalanche restore, symbol positions are already correct.
        // Since we didn't know the new reels length when we did the collapse,
        // we now need to set correct symbol data based on new reels length.
        if (!this._isAvalancheRestore) {
            for (let i: number = 0; i < this._collapsedReel.length; i++) {
                this._collapsedReel[i].reelPosition = reelLength;
                this._collapsedReel[i].reelTargetPosition = reelLength;
                reelLength--;
            }
        } else {
            this._isAvalancheRestore = false;
        }

        for (let i: number = 0; i < numberOfFillUpSymbols; i++) {
            const symbolName: string = TheBorder.model.gameData.reels[this._reelId][i];
            let sizeMultiplier: number = 1;
            if (symbolName.indexOf("_H") > -1) {
                sizeMultiplier = 0.5;
            }
            const symbol: ISymbol = this.createSymbol(symbolName, TheBorderSymbolStates.SPIN_IN, {
                reelId: this._reelId,
                startPosition: (0 - ((numberOfFillUpSymbols - i))),
                targetPosition: i + 1,
                sizeMultiplier: sizeMultiplier
            });
            this._collapsedReel.push(symbol);
        }
        this.moveScatterToTop(this._collapsedReel);

        return this.getFillUpAnimation();
    }

    /**
     * Calculates which symbols that should fall down and how many steps
     */
    public collapseReel(): gsap.core.Timeline {
        //We can't use reelTargetPosition since we don't know the size of the new reel (yet)
        this._collapsedReel = [];
        let counter: number = 500;
        for (let i: number = this._allOutSymbols.length - 1; i > -1; i--) {
            if (this._allOutSymbols[i].isRemoved) {
                (<TheBorderSymbol>this._allOutSymbols[i]).reset();
            } else {
                if (this._allOutSymbols[i].sizeMultiplier == 0.5) {
                    counter += 0.5 * TheBorderStatics.symbolHeight;
                }
                (<TheBorderSymbol>this._allOutSymbols[i]).collapseYPos = counter;
                counter -= TheBorderStatics.symbolHeight;
                this._collapsedReel.push(this._allOutSymbols[i]);
            }
        }
        return this.getCollapseAnimation();
    }

    public getAllInAnimation(): gsap.core.Timeline {
        const returnTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        let nearWinDelay: number = 0;
        this._allOutSymbols = [];

        returnTimeline.add(() => {
            if (this._reelHidden) {
                this._reelHidden = false;
                this.visible = true;
            }
        });

        while (this._allInSymbols.length > 0) {
            const symbol: ISymbol = this._allInSymbols.pop()!;
            this._allOutSymbols.unshift(symbol);
            if (symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.animateAllIn, symbol is null!`);
            } else {
                timeline = gsap.timeline();

                const fastSpin: boolean = (TheBorder.apiPlugin.settings.get(APISetting.FAST_SPIN, false));
                let totalSpinDuration = (fastSpin ? TheBorderStatics.mainAllInTotalSpinDurationFastSpin : TheBorderStatics.mainAllInTotalSpinDuration) - (this._reelId * (fastSpin ? TheBorderStatics.mainAllInReelAccelerationFastSpin : TheBorderStatics.mainAllInReelAcceleration));
                if (totalSpinDuration <= 0) {
                    totalSpinDuration = 0;
                }
                totalSpinDuration = TheBorderUtils.roundToDecimals(totalSpinDuration, 2);
                const symSpinStopDuration = fastSpin ? TheBorderStatics.mainReelSpinStopSymbolDelayFastSpin : TheBorderStatics.mainReelSpinStopSymbolDelay;
                const landingDelay = totalSpinDuration + (symSpinStopDuration + nearWinDelay);

                timeline.add([
                    gsap.timeline({delay: (fastSpin ? TheBorderStatics.mainReelAllInBlurDelayFastSpin + (symbol.reelTargetPosition * 0.002) - (this._reelId * 0.05) : (TheBorderStatics.mainReelAllInBlurDelay + (symbol.reelTargetPosition * 0.002)) - (this._reelId * 0.045))}).add((<TheBorderSymbol>symbol).getStateAnimation(TheBorderSymbolStates.LANDING, (0.06 - (this._reelId * 0.01)))),
                    gsap.to(symbol.position, {
                        duration: totalSpinDuration,
                        y: (TheBorderStatics.symbolHeight * symbol.sizeMultiplier) * (symbol.reelTargetPosition - 1),
                        ease: "power3.in",
                        delay: symSpinStopDuration + nearWinDelay
                    }),

                    gsap.timeline({delay: landingDelay}).add(() => {
                        this.symbolLandingSounds(symbol, false);
                        this.symbolLanding(symbol);
                    }),
                ]);

                timeline.add(gsap.to(symbol.position, {
                    duration: (totalSpinDuration * 0.16),
                    y: TheBorderStatics.symbolHeight * (symbol.reelTargetPosition - 1) - 2,
                    ease: "none"
                }));
                timeline.add(gsap.to(symbol.position, {
                    duration: (totalSpinDuration * 0.10),
                    y: TheBorderStatics.symbolHeight * (symbol.reelTargetPosition - 1),
                    ease: "none"
                }));

                timeline.add(() => {
                    symbol.reelPosition = symbol.reelTargetPosition
                });
                subTimelines.push(timeline);
            }
        }
        returnTimeline.add(subTimelines);
        return returnTimeline;
    }

    public stop(reelData: string[]): TimelineLite {
        const numberOfReelSymbols: number = TheBorder.model.reels[this._reelId].length;
        for (let i: number = 0; i < numberOfReelSymbols; i++) {
            const symbol: ISymbol = this.createSymbol(reelData[i], TheBorderSymbolStates.SPIN_IN, {
                reelId: this._reelId,
                startPosition: (0 - ((numberOfReelSymbols - i) + 1)),
                targetPosition: i + 1,
                sizeMultiplier: 1
            });
            this._allInSymbols.push(symbol);
        }
        this.moveScatterToTop(this._allInSymbols);
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(this.getAllInAnimation());

        return tl;
    }

    public removeAvalancheSymbol(symbol: ISymbol, resetSymbol: boolean = true): void {
        if (symbol.symbolName.indexOf("X_BOMB") === -1) {
            this.removeChild(<TheBorderSymbol>symbol);
            if (resetSymbol) {
                TheBorderReelView.resetSymbol(symbol);
            }
        } else {
            // #324
            for (let i: number = 0; i < (<TheBorderSymbol>symbol).children.length; i++) {
                if ((<TheBorderSymbol>symbol).children[i].name !== "topLayerContainer") {
                    (<TheBorderSymbol>symbol).children[i].visible = false;
                }
            }
            for (let i: number = 0; i < (<TheBorderSymbol>symbol).topLayerContainer.children.length; i++) {
                if ((<TheBorderSymbol>symbol).topLayerContainer.children[i].name !== "spineContainer") {
                    (<TheBorderSymbol>symbol).topLayerContainer.children[i].visible = false;
                }
            }
        }
    }

    // #324
    public removeBombSymbol(symbol: ISymbol): void {
        this.removeChild(<TheBorderSymbol>symbol);

        for (let i: number = 0; i < (<TheBorderSymbol>symbol).children.length; i++) {
            if ((<TheBorderSymbol>symbol).children[i].name !== "topLayerContainer") {
                (<TheBorderSymbol>symbol).children[i].visible = true;
            }
        }
        for (let i: number = 0; i < (<TheBorderSymbol>symbol).topLayerContainer.children.length; i++) {
            if ((<TheBorderSymbol>symbol).topLayerContainer.children[i].name !== "spineContainer") {
                (<TheBorderSymbol>symbol).topLayerContainer.children[i].visible = true;
            }
        }

        TheBorderReelView.resetSymbol(symbol);
    }

    private moveScatterToTop(symbols: ISymbol[]): void {
        for (let i: number = 0; i < symbols.length; i++) {
            if (symbols[i].symbolName == "BN") {
                this.addChild(<TheBorderSymbol>symbols[i]);
            }
        }
    }

    private getFillUpAnimation(): gsap.core.Timeline {
        const fillUpTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        let counter: number = 0;
        this._allOutSymbols = [];
        let highestTargetPosition: number = 0;
        let nearWinDelay: number = 0;
        for (let i: number = 0; i < this._collapsedReel.length; i++) {
            if (this._collapsedReel[i].reelTargetPosition > this._collapsedReel[i].reelPosition) {
                if (this._collapsedReel[i].reelTargetPosition > highestTargetPosition) {
                    highestTargetPosition = this._collapsedReel[i].reelTargetPosition;
                }
            }
        }

        while (this._collapsedReel.length > 0) {
            const symbol: ISymbol = this._collapsedReel.pop()!;
            this._allOutSymbols.push(symbol);
            if (symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.animateFillUp, symbol is null!`);
            } else {
                if (symbol.reelTargetPosition > symbol.reelPosition) {
                    timeline = gsap.timeline();
                    timeline.add([
                        gsap.timeline({delay: TheBorderReelView.SYMBOLS_FILL_UP_UNBLUR_DURATION}).add((<TheBorderSymbol>symbol).getStateAnimation(TheBorderSymbolStates.LANDING, TheBorderReelView.SYMBOLS_FILL_UP_UNBLUR_DURATION)),
                        gsap.to(symbol.position, {
                            duration: TheBorderReelView.SYMBOLS_FILL_UP_DURATION,
                            y: TheBorder.model.symbolYPositionsEvaluatedArea[this._reelId][symbol.reelTargetPosition - 1],
                            ease: "power3.in",
                            delay: (TheBorder.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? TheBorderStatics.mainReelSpinStopSymbolDelayFastSpin : TheBorderStatics.mainReelSpinStopSymbolDelay) + nearWinDelay
                        })
                    ]);

                    timeline.add(gsap.to(symbol.position, {
                        duration: 0.03,
                        y: TheBorder.model.symbolYPositionsEvaluatedArea[this._reelId][symbol.reelTargetPosition - 1] - 2,
                        ease: "none"
                    }));

                    if (symbol.reelTargetPosition == highestTargetPosition || nearWinDelay > 0) {
                        timeline.add(() => this.symbolLandingSounds(symbol, true));
                    } else {
                        timeline.add(() => this.symbolLandingSounds(symbol, false));
                    }

                    timeline.add(gsap.to(symbol.position, {
                        duration: 0.05,
                        y: TheBorder.model.symbolYPositionsEvaluatedArea[this._reelId][symbol.reelTargetPosition - 1],
                        ease: "none"
                    }));
                    timeline.add(() => this.symbolLanding(symbol));
                    timeline.add(() => {
                        symbol.reelPosition = symbol.reelTargetPosition
                    });
                    subTimelines.push(timeline);
                }
            }
            counter++;
        }
        fillUpTimeline.add(subTimelines);
        fillUpTimeline.add(() => {
            this._allOutSymbols = this.sortSymbolArray(this._allOutSymbols)
        });
        return fillUpTimeline;
    }

    private getCollapseAnimation(): gsap.core.Timeline {
        const collapseTimeline: gsap.core.Timeline = gsap.timeline();
        let timeline: gsap.core.Timeline;
        const subTimelines: gsap.core.Timeline[] = [];
        let highestTargetPosition: number = 0;

        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            if (this._allOutSymbols[i].reelTargetPosition > this._allOutSymbols[i].reelPosition) {
                if (this._allOutSymbols[i].reelTargetPosition > highestTargetPosition) {
                    highestTargetPosition = this._allOutSymbols[i].reelTargetPosition;
                }
            }
        }
        this._allOutSymbols.reverse();
        let isFirst: boolean = true;
        for (let i: number = 0; i < this._allOutSymbols.length; i++) {
            const symbol: TheBorderSymbol = <TheBorderSymbol>this._allOutSymbols[i];
            if (symbol == null) {
                debugger;
                throw new Error(`AvalancheReelPartAnimator.collapseAnimation, symbol is null!`);
            } else {
                timeline = gsap.timeline();
                timeline.add(symbol.animateCollapse());
                if (symbol.collapseYPos > symbol.position.y && isFirst) {
                    isFirst = false;
                    timeline.add(() => {
                        this.symbolLandingSounds(symbol, true);
                    });
                }
                subTimelines.push(timeline);
            }
        }
        this._allOutSymbols.reverse();

        collapseTimeline.add(subTimelines);

        if (collapseTimeline.duration() == 0) {
            collapseTimeline.add(gsap.to(this, {duration: 0.001}));
        }

        return collapseTimeline;
    }

    private symbolLandingSounds(symbol: ISymbol, playLandingSound: boolean): void {
        if ((symbol.reelTargetPosition == 6) || playLandingSound) {
            TheBorder.soundPlayer.playRandomReelStopSound(this._reelId);
        }
    }

    private symbolLanding(symbol: ISymbol): void {
        //Making sure the symbol goes un-blurred
        gsap.timeline({delay: 0.07}).add((<TheBorderSymbol>symbol).getStateAnimation(TheBorderSymbolStates.LANDING));

        if (symbol.symbolName === "X_BOMB") {
            (<TheBorderXBombSymbol>symbol).getLandingAndIdleAnimation().restart();
            if (!this._isBombLandSoundPlayed) {
                TheBorder.soundPlayer.playSound(TheBorderSound.BOMB_LAND);
                this._isBombLandSoundPlayed = true;
            }
        } else if (symbol.symbolName === "WR") {
            (<TheBorderXNudgeSymbol>symbol).getLandingAndIdleAnimation().restart();
            if (!this._isNudgeLandSoundPlayed) {
                TheBorder.soundPlayer.playSound(TheBorderSound.NUDGE_LAND);
                this._isNudgeLandSoundPlayed = true;
            }
        } else if (symbol.symbolName === "X_SPLIT" || symbol.symbolName === "X_SPLIT_H") {
            (<TheBorderXSplitSymbol>symbol).getLandingAndIdleAnimation().restart();
            if (!this._isSplitLandSoundPlayed) {
                TheBorder.soundPlayer.playSound(TheBorderSound.SPLIT_LAND);
                this._isSplitLandSoundPlayed = true;
            }
        } else if (symbol.symbolName === "X_WAYS") {
            (<TheBorderXWaysSymbol>symbol).getLandingAnimation().restart();
            if (!this._isWaysLandSoundPlayed) {
                TheBorder.soundPlayer.playSound(TheBorderSound.WAYS_LAND);
                this._isWaysLandSoundPlayed = true;
            }
        } else if (symbol.symbolName === "BN") {
            (<TheBorderScatterSymbol>symbol).getLandingAndIdleAnimation().restart();
            this.playScatterLandingSound();
        } else if (symbol.symbolName === "BN_H") {
            this.playScatterLandingSound();
        }
    }

    private sortSymbolArray(toBeSorted: ISymbol[]): ISymbol[] {
        const sortedSymbols: ISymbol[] = [];
        //Sorting symbols with reelposition 1-X (position 0 is top padding position)
        for (let i: number = 1; i < TheBorder.model.evaluatedArea[this._reelId].length + 1; i++) {
            for (let j: number = 0; j < toBeSorted.length; j++) {
                if (toBeSorted[j].reelPosition == i) {
                    sortedSymbols.push(toBeSorted[j]);
                    break;
                }
            }
        }
        return sortedSymbols;
    }

    private getFirstFullTopSymbol(): ISymbol {
        let retVal: ISymbol = this._allOutSymbols[0];

        if (retVal.symbolName.indexOf("_H") > -1) {
            for (let i: number = 1; i < this._allOutSymbols.length; i++) {
                if (this._allOutSymbols[i].symbolName.indexOf("_H") === -1) {
                    return this._allOutSymbols[i];
                }
            }
        }

        return retVal;
    }

    private getFirstFullBottomSymbol(): ISymbol {
        let retVal: ISymbol = this._allOutSymbols[this._allOutSymbols.length - 1];

        if (retVal.symbolName.indexOf("_H") > -1) {
            for (let i: number = (this._allOutSymbols.length - 2); i > -1; i--) {
                if (this._allOutSymbols[i].symbolName.indexOf("_H") === -1) {
                    return this._allOutSymbols[i];
                }
            }
        }

        return retVal;
    }

    private playScatterLandingSound(): void {
        const allScatterSymbols: TheBorderScatterSymbol[] = TheBorder.scatterSymbols.getAllUsedSymbols();
        let scatterCount: number = 0;
        allScatterSymbols.forEach((sym: TheBorderScatterSymbol) => {
            if (sym.reelId <= this._reelId) {
                scatterCount += sym.splitLevel;
            }
        });

        if (TheBorder.model.gameData.mode.indexOf(TheBorderGameMode.SUPER_FREESPIN) > -1) {
            TheBorder.soundPlayer.playSound(TheBorderSound.SCATTER_ADDITIONAL);
        } else if (TheBorder.model.gameData.mode.indexOf(TheBorderGameMode.FREESPIN) > -1 && scatterCount === 1) {
            TheBorder.soundPlayer.playSound(TheBorderSound.SCATTER_UNLOCK_SFS);
        } else {
            let soundName: string = "";
            if (scatterCount === 1) {
                soundName = (TheBorder.IS_BOOSTED_BET_ON ? TheBorderSound.SCATTER_LAND_SOUND_X_RIP : TheBorderSound.SCATTER_LAND_SOUNDS[0]);
            } else if (scatterCount > 1 && scatterCount < 5) {
                soundName = TheBorderSound.SCATTER_LAND_SOUNDS[scatterCount - 1];
            } else if (scatterCount === 6) {
                soundName = TheBorderSound.SCATTER_UNLOCK_FS;
            } else if (scatterCount === 7) {
                soundName = TheBorderSound.SCATTER_UNLOCK_SFS;
                TheBorder.soundPlayer.fadeOutEffect({
                    name: TheBorderSound.SCATTER_UNLOCK_FS,
                    duration: 50,
                    to: 0
                });
            } else if (scatterCount > 7) {
                soundName = TheBorderSound.SCATTER_ADDITIONAL;
            }
            if (soundName) {
                TheBorder.soundPlayer.playSound(soundName);
            }
        }
    }
}